import { useState, useEffect, useCallback } from 'react';

const ADMIN_KEY = 'pavan@123';

function loadList(storageKey) {
  try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); }
  catch { return []; }
}

function toCsv(rows, columns) {
  const header = columns.map(c => c.label).join(',');
  const body = rows.map(r => columns.map(c => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadCsv(filename, rows, columns) {
  const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function DataTable({ title, rows, columns, onExport, onClear }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>{title} ({rows.length})</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onExport} disabled={!rows.length}>Export CSV</button>
          <button onClick={onClear} disabled={!rows.length}>Clear</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p style={{ color: '#888' }}>No entries yet on this device/browser.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {columns.map(c => (
                  <th key={c.key} style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '6px 10px', whiteSpace: 'nowrap' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id ?? i}>
                  {columns.map(c => (
                    <td key={c.key} style={{ borderBottom: '1px solid #eee', padding: '6px 10px', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {String(r[c.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const LEAD_COLUMNS = [
  { key: 'time', label: 'Submitted' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'category', label: 'Category' },
  { key: 'message', label: 'Message' },
];

export default function Admin() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('pen_admin_ok') === '1');
  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);

  const refresh = useCallback(() => {
    setLeads(loadList('pen_leads'));
  }, []);

  useEffect(() => { if (authed) refresh(); }, [authed, refresh]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (keyInput === ADMIN_KEY) {
      sessionStorage.setItem('pen_admin_ok', '1');
      setAuthed(true);
      setError('');
    } else {
      setError('Incorrect admin key.');
    }
  };

  const clearList = (storageKey, setter) => {
    if (!window.confirm('Clear all entries on this device? This cannot be undone.')) return;
    localStorage.removeItem(storageKey);
    setter([]);
  };

  if (!authed) {
    return (
      <div style={{ maxWidth: 360, margin: '80px auto', fontFamily: 'sans-serif', padding: '0 16px' }}>
        <h1 style={{ fontSize: 22 }}>Admin Login</h1>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            style={{ width: '100%', padding: 10, fontSize: 14, marginBottom: 10 }}
            autoFocus
          />
          {error && <p style={{ color: 'crimson', fontSize: 13 }}>{error}</p>}
          <button type="submit" style={{ width: '100%', padding: 10 }}>Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '40px auto', fontFamily: 'sans-serif', padding: '0 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24 }}>Penconnect Admin</h1>
        <button onClick={() => { sessionStorage.removeItem('pen_admin_ok'); setAuthed(false); }}>Log out</button>
      </div>
      <p style={{ color: '#888', fontSize: 13, marginTop: -12, marginBottom: 24 }}>
        Data is stored in this browser's localStorage only — it reflects submissions made on this device, not all visitors.
      </p>
      <DataTable
        title="Project Leads"
        rows={leads}
        columns={LEAD_COLUMNS}
        onExport={() => downloadCsv(`penconnect-leads-${Date.now()}.csv`, leads, LEAD_COLUMNS)}
        onClear={() => clearList('pen_leads', setLeads)}
      />
    </div>
  );
}
